/* Shared deterministic rules are also exercised by the local Node tests. */
(function (scope) {
  const validQuantity = (item, quantity) => Number.isInteger(quantity) && quantity >= (item.min || 1) && quantity % (item.increment || 1) === 0 && (!item.max || quantity <= item.max);
  const isChoice = item => item.type === 'choice';
  const eligible = (item, quantity) => isChoice(item) || Boolean(item.id && item.available && validQuantity(item, item.quantity * quantity));
  const normalize = value => String(value ?? '').trim().toLowerCase();
  const basinChoices = {
    'basin-type-above-counter': 'Above-Counter Basin',
    'basin-type-semi-inset': 'Semi-Inset Basin'
  };
  const basinGroups = {
    'above-counter-basin': 'Above-Counter Basin',
    'above-counter-basin-cut-out': 'Above-Counter Basin',
    'semi-inset-basin': 'Semi-Inset Basin',
    'semi-inset-basin-cut-out': 'Semi-Inset Basin'
  };
  const basinAllowed = (type, allowed) => !type || !allowed || allowed.includes(type);
  function matchesRule(rule, attributes, addonChoices) {
    const name = normalize(rule.attribute);
    const target = normalize(rule.value);
    if (!name || !target) return false;
    let values;
    if (rule.source === 'Product option') {
      values = attributes.filter(option => normalize(option.name) === name).map(option => option.value);
    } else if (rule.source === 'Addon choice') {
      values = addonChoices.get(name) || [];
    } else return false;
    return values.some(value => rule.operator === 'Equals' ? normalize(value) === target :
      rule.operator === 'Contains' && normalize(value).includes(target));
  }
  function resolve(groups, selected, mainId, quantity = 1, attributes = []) {
    const active = new Set([String(mainId)]);
    const activeOptions = new Set();
    const addonChoices = new Map();
    const allowedBasinTypes = groups.some(group => group.id === 'basin-type') ?
      groups.map(group => basinGroups[group.id]).filter(Boolean) : null;
    return groups.map(group => {
      const items = group.items.map(item => ({ ...item, compatibilityBlocked:
        !basinAllowed(group.id === 'basin-type' && isChoice(item) ? basinChoices[item.key] : null, allowedBasinTypes) }));
      const variantMatch = !group.when?.length || group.when.some(id => active.has(String(id)));
      const optionMatch = !group.whenOptions?.length || group.whenOptions.some(key => activeOptions.has(key));
      const ruleMatch = group.rules?.length ? (group.ruleMode === 'All (AND)' ?
        group.rules.every(rule => matchesRule(rule, attributes, addonChoices)) :
        group.rules.some(rule => matchesRule(rule, attributes, addonChoices))) : variantMatch;
      const basinType = basinGroups[group.id];
      const basinTypeMatch = !allowedBasinTypes || !basinType ||
        Object.keys(basinChoices).some(key => basinChoices[key] === basinType && activeOptions.has(key));
      const visible = Boolean(ruleMatch && optionMatch && basinTypeMatch);
      const choices = visible ? items.filter(item => !item.compatibilityBlocked && eligible(item, quantity) && (selected[group.id] || []).includes(item.key)) : [];
      const chosen = group.mode === 'multiple' ? choices : choices.slice(0, 1);
      chosen.forEach(item => { activeOptions.add(item.key); if (!isChoice(item)) active.add(String(item.id)); });
      if (visible && chosen.length) {
        const title = normalize(group.title);
        addonChoices.set(title, [...(addonChoices.get(title) || []), ...chosen.map(item => item.label)]);
      }
      return { ...group, items, visible, chosen };
    });
  }
  function buildItems(main, quantity, groups, token, properties = {}, sellingPlan) {
    if (!main?.available || !Number.isInteger(quantity) || quantity < 1) throw new Error('Please choose an available product variant.');
    const missing = groups.find(group => group.visible && group.required && !group.chosen.length);
    if (missing) throw new Error(`Please choose an option for ${missing.title}.`);
    const extras = groups.flatMap(group => group.chosen.filter(item => !isChoice(item)).map(item => ({ group, item })));
    const parent = { id: main.id, quantity, properties: { ...properties } };
    groups.forEach(group => {
      const choices = group.chosen.filter(isChoice);
      if (choices.length) parent.properties[group.title] = choices.map(item => item.label).join(', ');
    });
    if (sellingPlan) parent.selling_plan = sellingPlan;
    if (!extras.length) return [parent];
    Object.assign(parent.properties, { _mbs_set: token, _mbs_role: 'parent', _mbs_units: '1' });
    return [parent, ...extras.map(({ group, item }) => ({
      id: item.id, quantity: quantity * item.quantity,
      properties: { _mbs_set: token, _mbs_role: 'extra', _mbs_units: String(item.quantity), _mbs_group: group.id, _mbs_option: item.key, 'Selected option': item.label }
    }))];
  }
  const model = { resolve, buildItems, validQuantity, isChoice, eligible, matchesRule };
  if (typeof module !== 'undefined' && module.exports) module.exports = model;
  scope.MbsAddonModel = model;
  if (typeof document === 'undefined') return;

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };
  const positionLabel = item => {
    if (!/basin hole/i.test(item.label)) return '';
    if (/without taphole/i.test(item.label)) return 'No Taphole';
    const clock = item.label.match(/\b(10|12|2)\s*o['’]clock/i);
    return clock ? `${clock[1]} o’clock` : '';
  };
  const selectedLabel = item => {
    if (isChoice(item)) return item.label;
    const position = positionLabel(item);
    if (position) return position;
    const variant = item.variantTitle && item.variantTitle !== 'Default Title' ? item.variantTitle : '';
    if (/waste/i.test(item.productTitle || item.label) && variant) return variant;
    if (/\bbasin\b/i.test(item.label) && !/hole|waste/i.test(item.label)) {
      const finish = variant || item.label.match(/\b(?:gloss|matte|matt)\s+.+?(?=\s+basin$|$)/i)?.[0] || '';
      let name = item.label.replace(/\b\d+(?:\.\d+)?\s*[x×]\s*\d+(?:\.\d+)?(?:\s*mm)?\b/gi, '').replace(/\s+basin$/i, '').trim();
      const finishIndex = finish ? name.toLowerCase().lastIndexOf(finish.toLowerCase()) : -1;
      if (finishIndex >= 0) name = `${name.slice(0, finishIndex)} ${name.slice(finishIndex + finish.length)}`;
      name = name.replace(/\s+/g, ' ').trim();
      return finish ? `${name} · ${finish}` : name;
    }
    return item.label || item.productTitle;
  };
  class ProductAddons extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-addon-config]').textContent);
      this.config.groups.forEach(group => { group.items = group.items.filter(item => isChoice(item) || !item.salesMode || item.salesMode === 'standard'); });
      this.root = this.closest('.mbs-product-information-v2');
      this.form = this.root.querySelector('form[data-type="add-to-cart-form"]');
      this.selected ||= {};
      this.format = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', { style: 'currency', currency: this.config.currency, currencyDisplay: 'narrowSymbol' });
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.build();
      document.addEventListener('keydown', event => { if (event.key === 'Escape') this.hidePreview(); }, options);
      document.addEventListener('pointerdown', event => { if (!this.contains(event.target) && !this.preview?.contains(event.target)) this.hidePreview(); }, options);
      window.addEventListener('resize', () => this.hidePreview(), options);
      window.addEventListener('scroll', () => this.hidePreview(), { ...options, passive: true });
      this.root.addEventListener('change', () => queueMicrotask(() => this.update()), options);
      this.root.addEventListener('click', event => {
        if (!event.target.closest('[data-btn-addtocart], #product-add-to-cart')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.submit();
      }, { ...options, capture: true });
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.submit();
      }, { ...options, capture: true });
      this.update();
      const priceHost = this.root.querySelector('[id^="product-price-"]');
      if (priceHost) {
        this.priceObserver = new MutationObserver(() => this.updatePrice());
        this.priceObserver.observe(priceHost, { childList: true, subtree: true, characterData: true });
      }
    }
    disconnectedCallback() { this.abort?.abort(); this.priceObserver?.disconnect(); clearTimeout(this.previewTimer); this.preview?.remove(); this.preview = null; this.initialized = false; }
    money(cents) { return this.format.format(cents / 100).replace(/([.,])00$/, ''); }
    hidePreview() {
      clearTimeout(this.previewTimer);
      if (this.preview) this.preview.hidden = true;
      this.previewInput?.removeAttribute('aria-describedby');
    }
    showPreview(item, label, input) {
      clearTimeout(this.previewTimer);
      this.previewInput?.removeAttribute('aria-describedby');
      if (!this.preview) {
        this.preview = el('div', 'mbs-addons-preview');
        this.preview.id = `addon-preview-${crypto.randomUUID()}`;
        this.preview.setAttribute('role', 'tooltip');
        this.preview.addEventListener('pointerenter', () => clearTimeout(this.previewTimer));
        this.preview.addEventListener('pointerleave', () => this.hidePreview());
        document.body.append(this.preview);
      }
      this.preview.replaceChildren();
      if (item.image) {
        const image = el('img', 'mbs-addons-preview__image');
        image.src = item.previewImage || item.image;
        image.alt = '';
        image.width = 280; image.height = 230;
        this.preview.append(image);
      }
      this.preview.append(el('strong', 'mbs-addons-preview__title', item.productTitle || item.label));
      const finish = item.variantTitle && item.variantTitle !== 'Default Title' ? item.variantTitle : item.label;
      if (finish !== (item.productTitle || item.label)) this.preview.append(el('span', 'mbs-addons-preview__finish', finish));
      if (item.sku?.trim()) this.preview.append(el('span', 'mbs-addons-preview__sku', `SKU ${item.sku.trim()}`));
      this.preview.append(el('strong', 'mbs-addons-preview__price', this.money(item.price * item.quantity)));
      if (item.compareAt > item.price) {
        const saving = (item.compareAt - item.price) * item.quantity;
        this.preview.append(el('span', 'mbs-addons-preview__saving', `${this.money(saving)} (-${Math.round((item.compareAt - item.price) / item.compareAt * 100)}%) off`));
        this.preview.append(el('s', 'mbs-addons-preview__rrp', `RRP of ${this.money(item.compareAt * item.quantity)}`));
      }
      if (item.description) this.preview.append(el('p', 'mbs-addons-preview__note', item.description));
      if (!item.available) this.preview.append(el('p', 'mbs-addons-preview__note', 'Currently unavailable'));
      const content = el('div', 'mbs-addons-preview__content');
      content.append(...this.preview.childNodes);
      this.preview.append(content);
      this.preview.hidden = false;
      const rect = label.getBoundingClientRect();
      const width = Math.min(280, window.innerWidth - 24);
      this.preview.style.width = `${width}px`;
      const naturalHeight = this.preview.getBoundingClientRect().height;
      const above = Math.max(0, rect.top - 24);
      const below = Math.max(0, window.innerHeight - rect.bottom - 24);
      const onTop = above >= naturalHeight || above >= below;
      content.style.maxHeight = `${Math.max(40, (onTop ? above : below) - 2)}px`;
      const height = this.preview.getBoundingClientRect().height;
      const left = Math.max(12, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 12));
      this.preview.style.left = `${left}px`;
      this.preview.style.top = `${onTop ? rect.top - height - 12 : rect.bottom + 12}px`;
      this.preview.dataset.placement = onTop ? 'above' : 'below';
      this.preview.style.setProperty('--addon-arrow-x', `${Math.max(18, Math.min(width - 18, rect.left + rect.width / 2 - left))}px`);
      this.previewInput = input;
      input.setAttribute('aria-describedby', this.preview.id);
    }
    current() {
      const picker = this.root.querySelector('variant-radios, variant-selects');
      // An initialized picker may deliberately have no matching variant.
      // Do not fall back to the previous form ID for that combination.
      if (picker && 'currentVariant' in picker) return picker.currentVariant;
      return this.config.variants.find(variant => String(variant.id) === this.form.elements.id.value);
    }
    quantity() { return Number(this.form.elements.quantity?.value || 1); }
    build() {
      const host = this.querySelector('[data-addon-groups]');
      // Preview fixtures without server markup retain the legacy builder.
      if (!host.hasAttribute('data-addon-rendered')) return this.buildLegacy();
      const fields = [...host.querySelectorAll('.mbs-addons__group')];
      this.config.groups.forEach((group, index) => {
        const fieldset = fields[index];
        const list = fieldset.querySelector('.mbs-addons__options');
        const items = new Map(group.items.map(item => [item.key, item]));
        list.querySelectorAll('input').forEach(input => {
          const item = items.get(input.value);
          if (!item) return;
          const label = input.closest('.mbs-addons__option');
          // Keep initial server output visually identical to the client formatter.
          const price = label.querySelector('.mbs-addons__price');
          if (price && !isChoice(item)) price.textContent = `+${this.money(item.price * item.quantity)}`;
          input.addEventListener('change', () => {
            if (group.mode !== 'multiple' && input.checked) list.querySelectorAll('input').forEach(node => { if (node !== input) node.checked = false; });
            this.selected[group.id] = [...list.querySelectorAll('input:checked')].map(node => node.value).filter(Boolean);
            this.update();
          });
          if (!isChoice(item) && (!group.style || group.style === 'compact')) {
            label.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') this.showPreview(item, label, input); });
            label.addEventListener('pointerleave', () => { this.previewTimer = setTimeout(() => this.hidePreview(), 160); });
            input.addEventListener('focus', () => this.showPreview(item, label, input));
            input.addEventListener('blur', () => this.hidePreview());
            input.addEventListener('change', () => this.showPreview(item, label, input));
          }
        });
        const more = fieldset.querySelector('.mbs-addons__more');
        if (more) {
          more.disabled = false;
          const count = group.items.filter(item => item.id || isChoice(item)).length;
          more.addEventListener('click', () => {
            const expanded = more.getAttribute('aria-expanded') !== 'true';
            more.setAttribute('aria-expanded', String(expanded));
            list.querySelectorAll('[data-extra-option]').forEach(node => { node.hidden = !expanded; });
            more.textContent = expanded ? 'Show fewer' : `Show all ${count} options`;
          });
        }
      });
    }
    buildLegacy() {
      const host = this.querySelector('[data-addon-groups]');
      const descriptions = new Map([...this.querySelectorAll('template[data-addon-description]')].map(template => [template.dataset.addonDescription, template]));
      host.replaceChildren();
      this.config.groups.forEach((group, index) => {
        const fieldset = el('fieldset', 'mbs-addons__group');
        fieldset.dataset.group = group.id;
        if (group.style === 'service') fieldset.classList.add('mbs-addons__group--service');
        const legend = el('legend', 'mbs-addons__legend', group.title);
        legend.append(el('span', 'mbs-addons__selected', ''));
        if (group.required) legend.append(el('span', 'mbs-addons__requirement', 'Required'));
        fieldset.append(legend);
        const descriptionTemplate = descriptions.get(group.id);
        if (descriptionTemplate) {
          const description = el('div', 'mbs-addons__description');
          description.append(descriptionTemplate.content.cloneNode(true));
          fieldset.append(description);
        } else if (group.description) {
          fieldset.append(el('p', 'mbs-addons__description', group.description));
        }

        const list = el('div', `mbs-addons__options mbs-addons__options--${['horizontal', 'service'].includes(group.style) ? group.style : 'compact'}`);
        const options = group.items.filter(item => item.id || isChoice(item));
        if (options.length && options.every(item => positionLabel(item))) fieldset.classList.add('mbs-addons__group--positions');

        options.forEach((item, optionIndex) => {
          const label = el('label', 'mbs-addons__option');
          if (isChoice(item)) label.classList.add('mbs-addons__option--choice');
          if (!item.key) label.classList.add('mbs-addons__option--none');
          const input = el('input', 'mbs-addons__input');
          input.type = group.mode === 'multiple' || !group.required ? 'checkbox' : 'radio';
          input.name = `addon-${this.root.id}-${index}`;
          input.value = item.key;
          input.setAttribute('aria-label', isChoice(item) ? item.label : item.key ? `${item.label}, ${this.money(item.price * item.quantity)}` : item.label);
          input.checked = !item.key;
          input.disabled = !eligible(item, this.quantity());
          input.addEventListener('change', () => {
            if (group.mode !== 'multiple' && input.checked) list.querySelectorAll('input').forEach(node => { if (node !== input) node.checked = false; });
            this.selected[group.id] = [...list.querySelectorAll('input:checked')].map(node => node.value).filter(Boolean);
            this.update();
          });
          if (!isChoice(item) && item.key && (!group.style || group.style === 'compact')) {
            label.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') this.showPreview(item, label, input); });
            label.addEventListener('pointerleave', () => { this.previewTimer = setTimeout(() => this.hidePreview(), 160); });
            input.addEventListener('focus', () => this.showPreview(item, label, input));
            input.addEventListener('blur', () => this.hidePreview());
            input.addEventListener('change', () => this.showPreview(item, label, input));
          }
          const card = el('span', 'mbs-addons__card');
          if (item.image) {
            const image = el('img', 'mbs-addons__image');
            image.src = item.image;
            image.alt = '';
            image.width = 120;
            image.height = 120;
            image.loading = 'lazy';
            card.append(image);
          }
          const details = el('span', 'mbs-addons__details');
          details.append(el('strong', '', item.label));
          if (item.variantTitle && item.variantTitle !== 'Default Title' && item.variantTitle !== item.label) details.append(el('span', 'mbs-addons__note', item.variantTitle));
          if (item.key && !isChoice(item)) details.append(el('span', 'mbs-addons__price', `+${this.money(item.price * item.quantity)}`));
          if (item.quantity > 1) details.append(el('span', 'mbs-addons__note', `${item.quantity} per product`));
          if (item.description) details.append(el('span', 'mbs-addons__note', item.description));
          if (!isChoice(item) && !item.available) details.append(el('span', 'mbs-addons__note', 'Currently unavailable'));
          card.append(details);
          if (!isChoice(item) && !item.available) card.append(el('span', 'mbs-addons__unavailable', this.dataset.unavailableLabel || 'Unavailable'));
          const check = this.querySelector('[data-addon-check]')?.content.firstElementChild || this.root.querySelector('.mbs-pi-color-card__selected');
          if (check) card.append(check.cloneNode(true));
          label.append(input, card);
          if (optionIndex >= 8) { label.hidden = true; label.dataset.extraOption = ''; }
          list.append(label);
        });
        fieldset.append(list);
        if (options.length > 8) {
          const more = el('button', 'mbs-addons__more', `Show all ${options.length} options`);
          more.type = 'button';
          more.setAttribute('aria-expanded', 'false');
          more.addEventListener('click', () => {
            const expanded = more.getAttribute('aria-expanded') !== 'true';
            more.setAttribute('aria-expanded', String(expanded));
            list.querySelectorAll('[data-extra-option]').forEach(node => { node.hidden = !expanded; });
            more.textContent = expanded ? 'Show fewer' : `Show all ${options.length} options`;
          });
          fieldset.append(more);
        }
        host.append(fieldset);
      });
    }
    update() {
      const main = this.current();
      const variantConfig = this.config.variants.find(variant => String(variant.id) === String(main?.id));
      this.groups = resolve(this.config.groups, this.selected, main?.id, this.quantity(), variantConfig?.selectedOptions || []);
      this.groups.forEach((group, index) => {
        const fieldset = this.querySelectorAll('.mbs-addons__group')[index];
        fieldset.hidden = !group.visible;
        this.selected[group.id] = group.chosen.map(item => item.key);
        fieldset.querySelector('.mbs-addons__selected').textContent = group.chosen.map(selectedLabel).join(', ');
        fieldset.querySelectorAll('input').forEach(input => {
          const item = group.items.find(option => option.key === input.value);
          input.disabled = !group.visible || Boolean(item?.compatibilityBlocked) || (item ? !eligible(item, this.quantity()) : false);
          input.checked = input.value ? this.selected[group.id].includes(input.value) : !group.chosen.length;
          const label = input.closest('.mbs-addons__option');
          const blocked = Boolean(item?.compatibilityBlocked);
          label.classList.toggle('mbs-addons__option--incompatible', blocked);
          let note = label.querySelector('[data-addon-incompatible]');
          if (blocked && !note) {
            note = el('span', 'mbs-addons__note');
            note.dataset.addonIncompatible = '';
            note.id = `addon-incompatible-${crypto.randomUUID()}`;
            note.textContent = this.config.incompatibleBasinLabel;
            label.querySelector('.mbs-addons__details').append(note);
          }
          if (note) {
            note.hidden = !blocked;
            if (blocked) input.setAttribute('aria-describedby', note.id);
            else input.removeAttribute('aria-describedby');
          }
        });
        if (group.chosen.length || !group.visible) fieldset.removeAttribute('aria-invalid');
      });
      this.querySelector('[data-addon-summary]').hidden = true;
      this.updatePrice();
    }
    updatePrice() {
      const main = this.current();
      if (!main) return;
      const chosen = (this.groups || []).flatMap(group => group.chosen.filter(item => !isChoice(item)));
      const total = main.price + chosen.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const compare = Math.max(main.price, main.compare_at_price || 0) + chosen.reduce((sum, item) => sum + Math.max(item.price, item.compareAt || 0) * item.quantity, 0);
      const host = this.root.querySelector('.mbs-pi-price-display');
      if (!host) return;
      const setText = (node, text) => { if (node && node.textContent !== text) node.textContent = text; };
      const card = host.querySelector('[data-price-card]');
      const panelMoney = cents => `${this.format.format(cents / 100)}${host.dataset.priceCurrencyCode ? ` ${host.dataset.priceCurrencyCode}` : ''}`;
      host.querySelectorAll('.price__last .price-item').forEach(node => setText(node, card ? panelMoney(total) : this.money(total)));
      let savings = host.querySelector('.mbs-pi-price-display__savings');
      if (!savings && compare > total) {
        savings = el('div', 'mbs-pi-price-display__savings');
        const rrp = el('span', 'mbs-pi-price-display__compare');
        rrp.append(el('s'));
        savings.append(el('span', 'mbs-pi-price-display__discount'), rrp);
        host.append(savings);
      }
      if (savings) {
        savings.hidden = compare <= total;
        setText(savings.querySelector('.mbs-pi-price-display__discount'), `${this.money(compare - total)} (-${Math.round((compare - total) / compare * 100)}%) off`);
        setText(savings.querySelector('s'), `RRP of ${this.money(compare)}`);
      }
      if (card) {
        const onSale = compare > total;
        const summary = card.querySelector('[data-price-summary]');
        if (summary) summary.hidden = !onSale;
        setText(card.querySelector('[data-price-heading]'), card.dataset.promotionTitle || (onSale ? card.dataset.headingSale : card.dataset.headingRegular));
        setText(card.querySelector('[data-price-rrp]'), panelMoney(compare));
        setText(card.querySelector('[data-price-saving]'), panelMoney(Math.max(0, compare - total)));
        const percent = onSale && compare > 0 ? Math.round((compare - total) / compare * 100) : 0;
        setText(card.querySelector('[data-price-percent]'), card.dataset.discountLabel.replace('__percent__', String(percent)));
      }
    }
    async submit() {
      if (this.busy) return;
      const error = this.querySelector('[data-addon-error]');
      error.hidden = true;
      this.update();
      const missing = this.groups.find(group => group.visible && group.required && !group.chosen.length);
      if (missing) {
        const fieldset = [...this.querySelectorAll('fieldset')].find(node => node.dataset.group === missing.id);
        fieldset.setAttribute('aria-invalid', 'true');
        fieldset.querySelector('input:not(:disabled)')?.focus();
        error.textContent = `Please choose an option for ${missing.title}.`;
        error.hidden = false;
        return;
      }
      const button = this.form.querySelector('[type="submit"]');
      this.busy = true;
      button.disabled = true;
      button.classList.add('is-loading');
      try {
        const formData = new FormData(this.form);
        const properties = {};
        for (const [key, value] of formData) {
          const match = key.match(/^properties\[(.*)\]$/);
          if (match && typeof value === 'string') properties[match[1]] = value;
        }
        const items = buildItems(this.current(), this.quantity(), this.groups, crypto.randomUUID(), properties, formData.get('selling_plan'));
        items.slice(1).forEach(item => { item.properties.For = this.config.productTitle; });
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
        const result = await response.json();
        if (!response.ok || result.status >= 400) throw new Error(result.description || result.message || 'Unable to add this selection. Please review your cart before retrying.');
        window.location.assign(`${root}cart`);
      } catch (failure) {
        error.textContent = `${failure.message} Check your cart before retrying if the connection was interrupted.`;
        error.hidden = false;
      } finally {
        this.busy = false;
        button.disabled = !this.current()?.available;
        button.classList.remove('is-loading');
      }
    }
  }
  if (!customElements.get('mbs-product-addons')) customElements.define('mbs-product-addons', ProductAddons);
})(typeof window === 'undefined' ? globalThis : window);
